/**
 * BillingPlansPage - Platform admin plan management
 *
 * Features:
 * - List all billing plans
 * - Create new plans
 * - Edit existing plans
 * - Archive/activate plans
 * - Configure features and usage limits per plan
 * - Set pricing tiers
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit2,
  Archive,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Users,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { PlanEditor } from '@/components/admin/PlanEditor';

export interface BillingPlan {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  features: Record<string, any> | null;
  limits: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tenant_count?: number;
}

export default function BillingPlansPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BillingPlan | null>(null);

  // Fetch all billing plans
  const { data: plans, isLoading, error } = useQuery({
    queryKey: ['admin-billing-plans'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_list_billing_plans');
      if (error) throw error;
      return data as BillingPlan[];
    },
    enabled: !authLoading && isAdmin(),
  });

  // Archive/activate plan mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ planId, isActive }: { planId: string; isActive: boolean }) => {
      const { data, error } = await supabase.rpc('calie_admin_toggle_plan_active', {
        _plan_id: planId,
        _is_active: !isActive,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
      toast.success('Plan status updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update plan: ${error.message}`);
    },
  });

  // Redirect non-admins
  if (!authLoading && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setEditorOpen(true);
  };

  const handleEditPlan = (plan: BillingPlan) => {
    setEditingPlan(plan);
    setEditorOpen(true);
  };

  const handleToggleActive = (plan: BillingPlan) => {
    toggleActiveMutation.mutate({ planId: plan.id, isActive: plan.is_active });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 max-w-7xl">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Billing Plans
                </CardTitle>
                <CardDescription>
                  Create and manage billing plans with features, limits, and pricing tiers
                </CardDescription>
              </div>
              <Button onClick={handleCreatePlan}>
                <Plus className="h-4 w-4 mr-2" />
                Create Plan
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <p>Failed to load plans: {(error as Error).message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans List */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : plans && plans.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan Name</TableHead>
                        <TableHead>Monthly Price</TableHead>
                        <TableHead>Yearly Price</TableHead>
                        <TableHead>Tenants</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plans.map((plan) => (
                        <TableRow key={plan.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{plan.name}</div>
                              {plan.description && (
                                <div className="text-sm text-muted-foreground mt-1">
                                  {plan.description}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              {formatCurrency(plan.price_monthly)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {plan.price_yearly ? (
                              <div className="flex items-center gap-1">
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                {formatCurrency(plan.price_yearly)}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              {plan.tenant_count || 0}
                            </div>
                          </TableCell>
                          <TableCell>
                            {plan.is_active ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <Archive className="h-3 w-3 mr-1" />
                                Archived
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditPlan(plan)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(plan)}
                                disabled={toggleActiveMutation.isPending}
                              >
                                {plan.is_active ? (
                                  <Archive className="h-4 w-4" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {plans.map((plan) => (
                    <Card key={plan.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold">{plan.name}</h3>
                          {plan.is_active ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Archive className="h-3 w-3 mr-1" />
                              Archived
                            </Badge>
                          )}
                        </div>
                        {plan.description && (
                          <p className="text-sm text-muted-foreground">{plan.description}</p>
                        )}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Monthly:</span>
                            <div className="font-medium">{formatCurrency(plan.price_monthly)}</div>
                          </div>
                          {plan.price_yearly && (
                            <div>
                              <span className="text-muted-foreground">Yearly:</span>
                              <div className="font-medium">{formatCurrency(plan.price_yearly)}</div>
                            </div>
                          )}
                          <div>
                            <span className="text-muted-foreground">Tenants:</span>
                            <div className="font-medium">{plan.tenant_count || 0}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleEditPlan(plan)}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleToggleActive(plan)}
                            disabled={toggleActiveMutation.isPending}
                          >
                            {plan.is_active ? (
                              <>
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Activate
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No billing plans yet</h3>
                <p className="mb-4">Create your first billing plan to get started</p>
                <Button onClick={handleCreatePlan}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Plan
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plan Editor Dialog */}
      <PlanEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        plan={editingPlan}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
          setEditorOpen(false);
        }}
      />
    </MainLayout>
  );
}
